import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table, Button, Input, Select, Tag, Avatar, Tabs, Spin, Empty,
  Row, Col, Statistic, Card, Breadcrumb, message, Space, Tooltip,
} from "antd";
import {
  ArrowLeftOutlined, BankOutlined, MailOutlined, CheckCircleOutlined,
  SearchOutlined, DownloadOutlined, CalendarOutlined, TeamOutlined,
  FileTextOutlined, DollarOutlined, ClockCircleOutlined,
  AppstoreOutlined, BarsOutlined, EnvironmentOutlined, UserOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { API_CONST } from "../../../const";
import ScheduleInterviewModal from "../../../components/ScheduleInterviewModal/ScheduleInterviewModal";

const { Option } = Select;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const OrgCard = styled(Card)`
  border-radius: 12px;
  margin-bottom: 24px;
  border: 1px solid #e8e8e8;
  .ant-card-body { padding: 24px; }
`;

const StatsRow = styled(Row)`
  margin-top: 20px;
  border-top: 1px solid #f0f0f0;
  padding-top: 20px;
  .ant-statistic-title { font-size: 12px; color: #888; }
  .ant-statistic-content-value { font-size: 22px; color: #014c75; font-weight: 700; }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const DetailWrapper = styled.div`
  .section-tabs .ant-tabs-nav { margin-bottom: 0; }
`;

const TalentCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(252px, 1fr));
  gap: 16px;
`;

const TalentCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #e8f0fe;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s;
  &:hover {
    box-shadow: 0 4px 16px rgba(1, 76, 117, 0.12);
    transform: translateY(-2px);
  }
  .ant-card-body { padding: 14px 16px; }

  .tc-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;
  }
  .tc-name {
    font-size: 14px;
    font-weight: 700;
    color: #262626;
    margin: 0;
    line-height: 1.3;
  }
  .tc-role {
    font-size: 11px;
    color: #666;
    margin: 2px 0 0;
  }
  .tc-section-label {
    font-size: 10px;
    font-weight: 600;
    color: #aaa;
    letter-spacing: 0.5px;
    margin: 8px 0 3px;
  }
  .tc-skills {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    .ant-tag { margin: 0; font-size: 11px; border-radius: 4px; }
  }
  .tc-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
    font-size: 12px;
    color: #555;
  }
  .tc-meta-row {
    display: flex;
    align-items: center;
    gap: 6px;
    .anticon { color: #014c75; font-size: 12px; }
  }
  .tc-rate {
    font-size: 13px;
    font-weight: 700;
    color: #01d9a9;
  }
`;

const formatRate = (rate) => {
  if (!rate) return null;
  const n = Number(rate);
  if (isNaN(n)) return null;
  return `₹${n.toLocaleString("en-IN")}/mo`;
};

const getSkillName = (s) => (typeof s === "string" ? s : s?.skill || "");

const CustomerPartnerDetail = () => {
  const { relationId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const headers = { Authorization: `Bearer ${token}` };

  const [relation, setRelation] = useState(null);
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  const [talents, setTalents] = useState([]);
  const [talentLoading, setTalentLoading] = useState(false);
  const [talentPagination, setTalentPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(undefined);
  const [locationFilter, setLocationFilter] = useState(undefined);
  const [viewMode, setViewMode] = useState("cards");
  const searchDebounce = useRef(null);

  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  const [scheduleModal, setScheduleModal] = useState({ visible: false, talentId: null, talentName: "" });
  const [activeTab, setActiveTab] = useState("bench");

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(
        `${API_CONST.PARTNER_RELATION_PARTNER_STATS}/${relationId}`,
        { headers }
      );
      setRelation(res.data.relation || null);
      setStats(res.data.stats || {});
    } catch {
      message.error("Failed to load partner details");
    } finally {
      setStatsLoading(false);
    }
  }, [relationId]);

  const fetchTalents = useCallback(async (overrides = {}) => {
    setTalentLoading(true);
    try {
      const params = {
        page: talentPagination.page,
        pageSize: talentPagination.pageSize,
        ...(search ? { search } : {}),
        ...(roleFilter ? { role: roleFilter } : {}),
        ...(locationFilter ? { location: locationFilter } : {}),
        ...overrides,
      };
      const res = await axios.get(
        `${API_CONST.PARTNER_RELATION_BENCH_POOL}/${relationId}/bench-pool`,
        { headers, params }
      );
      setTalents(res.data.profiles || []);
      setTalentPagination((p) => ({ ...p, total: res.data.total || 0 }));
    } catch (err) {
      if (err.response?.status !== 400) message.error("Failed to load bench pool");
    } finally {
      setTalentLoading(false);
    }
  }, [relationId, talentPagination.page, talentPagination.pageSize, search, roleFilter, locationFilter]);

  const fetchContracts = useCallback(async () => {
    if (!relation?.partnerOrganization) return;
    setContractsLoading(true);
    try {
      const res = await axios.post(
        API_CONST.GET_HIRING_RECORDS,
        { partnerOrganizations: [relation.partnerOrganization], status: undefined },
        { headers }
      );
      setContracts((res.data.hiringRecords || []).filter(r => r.status !== 'draft'));
    } catch {
      message.error("Failed to load contracts");
    } finally {
      setContractsLoading(false);
    }
  }, [relation?.partnerOrganization]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { if (activeTab === "bench") fetchTalents(); }, [activeTab, talentPagination.page, talentPagination.pageSize]);
  useEffect(() => { if (activeTab === "contracts" && relation?.partnerOrganization) fetchContracts(); }, [activeTab, relation]);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setTalentPagination((p) => ({ ...p, page: 1 }));
      fetchTalents({ search: val, page: 1 });
    }, 400);
  };

  const handleDownloadResume = async (e, talent) => {
    e.stopPropagation();
    try {
      const res = await axios.get(
        `${API_CONST.BRANDED_RESUME_BASE}/${talent.id}/branded-resume`,
        { headers }
      );
      if (res.data.downloadUrl) {
        window.open(res.data.downloadUrl, "_blank");
      } else {
        message.error("Could not get resume download URL");
      }
    } catch { message.error("Failed to download resume. Please try again."); }
  };

  const handleViewTalent = (rec) => {
    navigate(`/customer/talent-details/${rec.id}`, {
      state: { backPath: `/customer/my-partners/${relationId}`, backLabel: "Back to Partner" },
    });
  };

  const renderTalentCards = () => (
    <TalentCardGrid>
      {talents.map((rec) => {
        const primarySkills = (Array.isArray(rec.primarySkills) ? rec.primarySkills : []).slice(0, 3);
        const rate = formatRate(rec.monthlyRate || rec.clientRate || rec.rate);
        return (
          <TalentCard key={rec.talentId || rec.id} onClick={() => handleViewTalent(rec)}>
            <div className="tc-header">
              <Avatar
                size={44}
                style={{ background: "#e6f7ff", color: "#014c75", fontWeight: 700, flexShrink: 0, fontSize: 18 }}
              >
                {rec.name?.charAt(0)?.toUpperCase() || <UserOutlined />}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="tc-name">{rec.name}</p>
                <p className="tc-role">{rec.role}</p>
              </div>
            </div>

            {primarySkills.length > 0 && (
              <>
                <div className="tc-section-label">PRIMARY</div>
                <div className="tc-skills">
                  {primarySkills.map((s) => (
                    <Tag key={getSkillName(s)} color="cyan">{getSkillName(s)}</Tag>
                  ))}
                </div>
              </>
            )}

            <div className="tc-meta">
              {rec.totalExperience && (
                <span className="tc-meta-row">
                  <ClockCircleOutlined /> {rec.totalExperience}
                </span>
              )}
              {rec.availability && (
                <span className="tc-meta-row">
                  <CalendarOutlined />
                  <Tag color={rec.availability === "Immediate" ? "green" : "orange"} style={{ margin: 0, fontSize: 11 }}>
                    {rec.availability}
                  </Tag>
                </span>
              )}
              {rec.location && (
                <span className="tc-meta-row">
                  <EnvironmentOutlined /> {rec.location}
                </span>
              )}
              {rate && <span className="tc-rate">{rate}</span>}
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Download Resume">
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  disabled={!rec.resumeUrl}
                  onClick={(e) => handleDownloadResume(e, rec)}
                />
              </Tooltip>
              <Tooltip title="Schedule Interview">
                <Button
                  size="small"
                  icon={<CalendarOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setScheduleModal({ visible: true, talentId: rec.id, talentName: rec.name });
                  }}
                  style={{ color: "#014c75", borderColor: "#014c75" }}
                />
              </Tooltip>
            </div>
          </TalentCard>
        );
      })}
    </TalentCardGrid>
  );

  const talentColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, rec) => (
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
          onClick={() => handleViewTalent(rec)}
        >
          <Avatar size={32} style={{ background: "#e6f7ff", color: "#014c75", fontSize: 12 }}>
            {name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, color: "#014c75", fontSize: 13 }}>{name}</div>
            <div style={{ fontSize: 11, color: "#888" }}>{rec.talentId}</div>
          </div>
        </div>
      ),
    },
    { title: "Role", dataIndex: "role", key: "role", render: (v) => <Tag color="blue">{v}</Tag> },
    {
      title: "Primary Skills",
      dataIndex: "primarySkills",
      key: "primarySkills",
      render: (skills) =>
        (Array.isArray(skills) ? skills : []).slice(0, 3).map((s) => (
          <Tag key={getSkillName(s)} style={{ marginBottom: 2 }}>{getSkillName(s)}</Tag>
        )),
    },
    { title: "Location", dataIndex: "location", key: "location" },
    { title: "Experience", dataIndex: "totalExperience", key: "totalExperience" },
    {
      title: "Availability",
      dataIndex: "availability",
      key: "availability",
      render: (v) => <Tag color={v === "Immediate" ? "green" : "orange"}>{v || "—"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, rec) => (
        <Space>
          <Tooltip title="Download Resume">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={(e) => handleDownloadResume(e, rec)}
              disabled={!rec.resumeUrl}
            />
          </Tooltip>
          <Tooltip title="Schedule Interview">
            <Button
              size="small"
              icon={<CalendarOutlined />}
              onClick={() => setScheduleModal({ visible: true, talentId: rec.id, talentName: rec.name })}
              style={{ color: "#014c75", borderColor: "#014c75" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const contractColumns = [
    { title: "Contract ID", dataIndex: "id", key: "id", width: 180, render: (v) => <span style={{ fontSize: 11, fontFamily: "monospace" }}>{v}</span> },
    { title: "Talent", dataIndex: "talentName", key: "talentName", render: (v) => <strong>{v || "—"}</strong> },
    { title: "Role", dataIndex: "talentRole", key: "talentRole" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => {
        const colorMap = { active: "green", terminated: "red", completed: "blue", draft: "orange" };
        return <Tag color={colorMap[v] || "default"}>{v}</Tag>;
      },
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (v) => v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
    },
    {
      title: "Partner Rate",
      dataIndex: "partnerRate",
      key: "partnerRate",
      render: (v, rec) => v ? `${rec.currency || "INR"} ${Number(v).toLocaleString()}/mo` : "—",
    },
  ];

  const partnerName = relation?.partnerOrganization || "Partner";

  return (
    <DetailWrapper>
      <PageHeader>
        <Breadcrumb
          items={[
            { title: <span onClick={() => navigate("/customer/my-partners")} style={{ cursor: "pointer", color: "#014c75" }}>My Partners</span> },
            { title: partnerName },
          ]}
          style={{ marginBottom: 12 }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/customer/my-partners")}>Back</Button>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#014c75" }}>{partnerName}</h1>
        </div>
      </PageHeader>

      <Spin spinning={statsLoading}>
        <OrgCard>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Avatar size={54} icon={<BankOutlined />} style={{ background: "#e6f7ff", color: "#014c75" }} />
            <div>
              <h2 style={{ margin: 0, color: "#014c75", fontSize: 18 }}>{partnerName}</h2>
              {relation?.invitedEmail && (
                <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>
                  <MailOutlined style={{ marginRight: 6 }} />{relation.invitedEmail}
                </div>
              )}
              {relation?.acceptedAt && (
                <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
                  <CheckCircleOutlined style={{ marginRight: 6 }} />
                  Partner since {new Date(relation.acceptedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              )}
            </div>
            <div style={{ marginLeft: "auto" }}>
              <Tag color={relation?.partnerOrganization ? "green" : "cyan"} icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: "4px 10px" }}>
                {relation?.partnerOrganization ? "On Platform" : "Offline Partner"}
              </Tag>
            </div>
          </div>

          <StatsRow gutter={[0, 0]}>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Bench Pool" value={stats.benchCount ?? 0} prefix={<TeamOutlined />} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Active Hires" value={stats.activeHires ?? 0} valueStyle={{ color: "#52c41a" }} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Submitted" value={stats.applicationsSubmitted ?? 0} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Hired" value={stats.hiredCount ?? 0} valueStyle={{ color: "#014c75" }} />
            </Col>
            <Col span={4} style={{ textAlign: "center", borderRight: "1px solid #f0f0f0" }}>
              <Statistic title="Rejected" value={stats.applicationsRejected ?? 0} valueStyle={{ color: "#ff4d4f" }} />
            </Col>
            <Col span={4} style={{ textAlign: "center" }}>
              <Statistic title="Shortlisted" value={stats.talentsShortlisted ?? 0} valueStyle={{ color: "#fa8c16" }} />
            </Col>
          </StatsRow>
        </OrgCard>
      </Spin>

      <Tabs
        className="section-tabs"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "bench",
            label: <span><TeamOutlined /> Bench Pool</span>,
            children: (
              <Card style={{ borderRadius: 8 }}>
                <FilterBar>
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Search by name, role, or skill…"
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{ width: 260 }}
                    allowClear
                  />
                  <Select
                    placeholder="Filter by Role"
                    value={roleFilter}
                    onChange={(v) => { setRoleFilter(v); setTimeout(() => fetchTalents({ role: v, page: 1 }), 0); }}
                    style={{ width: 180 }}
                    allowClear
                  >
                    {[...new Set(talents.map((t) => t.role).filter(Boolean))].map((r) => (
                      <Option key={r} value={r}>{r}</Option>
                    ))}
                  </Select>
                  <Select
                    placeholder="Filter by Location"
                    value={locationFilter}
                    onChange={(v) => { setLocationFilter(v); setTimeout(() => fetchTalents({ location: v, page: 1 }), 0); }}
                    style={{ width: 160 }}
                    allowClear
                  >
                    {[...new Set(talents.map((t) => t.location).filter(Boolean))].map((l) => (
                      <Option key={l} value={l}>{l}</Option>
                    ))}
                  </Select>
                  <Button onClick={() => { setSearch(""); setRoleFilter(undefined); setLocationFilter(undefined); fetchTalents({ search: "", page: 1 }); }}>
                    Clear Filters
                  </Button>

                  <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                    <Tooltip title="Card view">
                      <Button
                        icon={<AppstoreOutlined />}
                        type={viewMode === "cards" ? "primary" : "default"}
                        style={viewMode === "cards" ? { background: "#014c75", borderColor: "#014c75" } : {}}
                        onClick={() => setViewMode("cards")}
                      />
                    </Tooltip>
                    <Tooltip title="List view">
                      <Button
                        icon={<BarsOutlined />}
                        type={viewMode === "list" ? "primary" : "default"}
                        style={viewMode === "list" ? { background: "#014c75", borderColor: "#014c75" } : {}}
                        onClick={() => setViewMode("list")}
                      />
                    </Tooltip>
                  </div>
                </FilterBar>

                <Spin spinning={talentLoading}>
                  {talents.length === 0 && !talentLoading ? (
                    <Empty description="No active talents in bench pool" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : viewMode === "cards" ? (
                    <>
                      {renderTalentCards()}
                      {talentPagination.total > talentPagination.pageSize && (
                        <div style={{ textAlign: "center", marginTop: 20 }}>
                          <Button.Group>
                            <Button
                              disabled={talentPagination.page <= 1}
                              onClick={() => setTalentPagination((p) => ({ ...p, page: p.page - 1 }))}
                            >
                              ‹ Prev
                            </Button>
                            <Button disabled style={{ cursor: "default" }}>
                              {talentPagination.page} / {Math.ceil(talentPagination.total / talentPagination.pageSize)}
                            </Button>
                            <Button
                              disabled={talentPagination.page >= Math.ceil(talentPagination.total / talentPagination.pageSize)}
                              onClick={() => setTalentPagination((p) => ({ ...p, page: p.page + 1 }))}
                            >
                              Next ›
                            </Button>
                          </Button.Group>
                          <div style={{ marginTop: 6, fontSize: 12, color: "#888" }}>
                            {talentPagination.total} talents total
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Table
                      dataSource={talents}
                      columns={talentColumns}
                      rowKey="talentId"
                      loading={false}
                      pagination={{
                        current: talentPagination.page,
                        pageSize: talentPagination.pageSize,
                        total: talentPagination.total,
                        showSizeChanger: true,
                        showTotal: (t) => `${t} talents`,
                        onChange: (page, pageSize) => setTalentPagination((p) => ({ ...p, page, pageSize })),
                      }}
                      locale={{ emptyText: <Empty description="No active talents in bench pool" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                      size="middle"
                    />
                  )}
                </Spin>
              </Card>
            ),
          },
          {
            key: "contracts",
            label: <span><FileTextOutlined /> Contracts</span>,
            children: (
              <Card style={{ borderRadius: 8 }}>
                {contracts.length > 0 && (
                  <Row gutter={16} style={{ marginBottom: 20 }}>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#f6ffed", borderColor: "#b7eb8f" }}>
                        <Statistic title="Active" value={contracts.filter((c) => c.status === "active").length} valueStyle={{ color: "#52c41a", fontSize: 24 }} prefix={<CheckCircleOutlined />} />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#e6f7ff", borderColor: "#91d5ff" }}>
                        <Statistic title="Total Contracts" value={contracts.length} valueStyle={{ color: "#1890ff", fontSize: 24 }} prefix={<FileTextOutlined />} />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#fff7e6", borderColor: "#ffd591" }}>
                        <Statistic
                          title="Total Spend"
                          value={contracts.filter((c) => c.status === "active").reduce((s, c) => s + (Number(c.settledClientRate) || Number(c.clientRate) || 0), 0).toLocaleString()}
                          prefix={<DollarOutlined />}
                          suffix="/mo"
                          valueStyle={{ color: "#fa8c16", fontSize: 20 }}
                        />
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" style={{ textAlign: "center", background: "#f9f0ff", borderColor: "#d3adf7" }}>
                        <Statistic title="Completed" value={contracts.filter((c) => c.status === "completed").length} valueStyle={{ color: "#722ed1", fontSize: 24 }} prefix={<ClockCircleOutlined />} />
                      </Card>
                    </Col>
                  </Row>
                )}

                <Table
                  dataSource={contracts}
                  columns={contractColumns}
                  rowKey="id"
                  loading={contractsLoading}
                  pagination={{ pageSize: 10, showTotal: (t) => `${t} contracts` }}
                  locale={{ emptyText: <Empty description="No contracts yet" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                  size="middle"
                />
              </Card>
            ),
          },
        ]}
      />

      <ScheduleInterviewModal
        visible={scheduleModal.visible}
        talentId={scheduleModal.talentId}
        talentName={scheduleModal.talentName}
        onClose={() => setScheduleModal({ visible: false, talentId: null, talentName: "" })}
        onSuccess={() => { setScheduleModal({ visible: false, talentId: null, talentName: "" }); message.success("Interview scheduled"); }}
      />
    </DetailWrapper>
  );
};

export default CustomerPartnerDetail;
